import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { SlidersHorizontal } from '@phosphor-icons/react';
import PlaceCard from '../components/PlaceCard';
import AdCard from '../components/AdCard';
import FilterModal from '../components/FilterModal';
import ActiveFiltersChips from '../components/ActiveFiltersChips';
import Loader from '../components/Loader';
import ContactFooter from '../components/ContactFooter';
import { useCategories } from '../hooks/useCategories';
import { useFilters } from '../hooks/useFilters';
import { useFilteredPlaces } from '../hooks/useFilteredPlaces';
import { useScrollRestoration } from '../hooks/useScrollRestoration';
import { getAds } from '../services/api';
import telegramService from '../services/telegram';

const CategoryPage = () => {
  const { slug } = useParams();
  const scrollRef = useScrollRestoration();
  const { categories } = useCategories();
  const [currentCategory, setCurrentCategory] = useState(null);
  const [isFilterModalOpen, setIsFilterModalOpen] = useState(false);
  const [ads, setAds] = useState([]);
  const [isInitialLoad, setIsInitialLoad] = useState(true);

  // Фильтры
  const {
    selectedTagIds,
    availableTags,
    toggleTag,
    clearTags,
    setSelectedTags,
    getSelectedCount,
  } = useFilters(currentCategory?.id);

  // Отфильтрованные места
  const { places, loading } = useFilteredPlaces(
    currentCategory?.id,
    selectedTagIds,
    null,
    200
  );

  // Получить полные объекты выбранных тегов для чипсов
  const selectedTagObjects = availableTags.filter((tag) =>
    selectedTagIds.includes(tag.id)
  );

  useEffect(() => {
    if (categories.length === 0) return;

    const category = categories.find((cat) => cat.slug === slug);
    if (category && category.id !== currentCategory?.id) {
      setCurrentCategory(category);
      clearTags();
    }
  }, [slug, categories, currentCategory?.id, clearTags]);

  // Сбросить isInitialLoad после загрузки мест
  useEffect(() => {
    if (!loading && currentCategory && places.length >= 0) {
      setIsInitialLoad(false);
    }
  }, [loading, currentCategory, places.length]);

  // Загрузка рекламных объявлений
  useEffect(() => {
    const loadAds = async () => {
      if (currentCategory?.id) {
        const response = await getAds(currentCategory.id, 'categories');
        const sortedAds = (response.data || []).sort((a, b) => (a.categoriesOrder || 0) - (b.categoriesOrder || 0));
        setAds(sortedAds);
      }
    };
    loadAds();
  }, [currentCategory?.id]);

  const handleFilter = () => {
    telegramService.hapticFeedback('light');
    setIsFilterModalOpen(true);
  };

  const handleCloseFilterModal = () => {
    setIsFilterModalOpen(false);
  };

  const handleApplyFilters = (tagIds) => {
    setSelectedTags(tagIds);
  };

  const handleRemoveTag = (tagId) => {
    toggleTag(tagId);
  };

  const handleClearAllFilters = () => {
    clearTags();
  };


  const filterCount = getSelectedCount();
  const showLoader = isInitialLoad || loading || !currentCategory;
  const showContent = !isInitialLoad && !loading && currentCategory;

  return (
    <div ref={scrollRef} className="min-h-screen bg-theme-bg pb-6">
      {/* Title Block */}
      {currentCategory && (
        <section className="px-4 pb-4 flex items-center justify-between" style={{ paddingTop: 'calc(var(--safe-area-top) + var(--tg-header-height) + 16px)' }}>
          <div>
            <h1 className="text-[22px] font-bold text-theme-text-primary">
              {currentCategory.name}
            </h1>
            <p className="text-sm text-theme-text-secondary mt-1">
              Нажмите на кнопку «Фильтры», чтобы выбрать наиболее подходящее место
            </p>
          </div>
          <button
            onClick={handleFilter}
            className="p-2 hover:bg-theme-card-secondary rounded-full transition-colors relative flex-shrink-0"
          >
            <SlidersHorizontal size={24} className="text-theme-text-secondary" />
            {filterCount > 0 && (
              <span className="absolute -top-1 -right-1 bg-primary text-white text-xs w-5 h-5 rounded-full flex items-center justify-center font-bold">
                {filterCount}
              </span>
            )}
          </button>
        </section>
      )}

      {/* Active Filters */}
      {selectedTagObjects.length > 0 && (
        <ActiveFiltersChips
          selectedTags={selectedTagObjects}
          onRemoveTag={handleRemoveTag}
          onClearAll={handleClearAllFilters}
        />
      )}

      {/* Places Grid */}
      <section className="px-lg mt-4">
        {/* Loading state */}
        {showLoader && <Loader size="large" />}

        {/* Places grid */}
        {showContent && places.length > 0 && (
          <div className="grid grid-cols-2 gap-4">
              {(() => {
                const result = [];
                const adShowCounts = {}; // Счетчик показов для каждого объявления
                let nextAdIndex = 0; // Индекс следующего объявления по order
                let placesUntilNextAd = ads.length > 0 ? (ads[0].categoriesShowEveryNPlaces || 3) : 0; // Через сколько мест показать следующее объявление

                // Инициализируем счетчики
                ads.forEach(ad => {
                  adShowCounts[ad.id] = 0;
                });

                places.forEach((place, placeIndex) => {
                  result.push(<PlaceCard key={place.id} place={place} />);

                  // Логика показа рекламы - последовательно по order
                  if (ads.length > 0) {
                    placesUntilNextAd--;

                    // Если пора показать объявление
                    if (placesUntilNextAd === 0) {
                      const ad = ads[nextAdIndex];
                      const repeatCount = ad.categoriesRepeatCount || 1;

                      // Проверяем, не превысили ли repeatCount
                      if (adShowCounts[ad.id] < repeatCount) {
                        result.push(
                          <AdCard
                            key={`ad-${ad.id}-${placeIndex}`}
                            ad={ad}
                            source="categories"
                          />
                        );
                        adShowCounts[ad.id]++;
                      }

                      // Переходим к следующему объявлению
                      nextAdIndex++;

                      // Если дошли до конца списка объявлений
                      if (nextAdIndex >= ads.length) {
                        // Проверяем, есть ли ещё объявления для повтора
                        const hasMoreToShow = ads.some(ad => adShowCounts[ad.id] < (ad.categoriesRepeatCount || 1));

                        if (hasMoreToShow) {
                          // Начинаем новый круг - возвращаемся к первому объявлению
                          nextAdIndex = 0;
                        } else {
                          // Все объявления показаны нужное количество раз - больше не показываем
                          placesUntilNextAd = Infinity;
                        }
                      }

                      // Устанавливаем интервал для следующего объявления
                      if (placesUntilNextAd !== Infinity) {
                        placesUntilNextAd = ads[nextAdIndex]?.categoriesShowEveryNPlaces || 3;
                      }
                    }
                  }
                });

                return result;
              })()}
          </div>
        )}

        {/* Empty state */}
        {showContent && places.length === 0 && (
          <div className="bg-theme-card-secondary rounded-medium p-xxl text-center">
            <p className="text-theme-text-secondary text-body">
              {filterCount > 0
                ? 'Нет мест с выбранными фильтрами'
                : 'Нет мест в этой категории'}
            </p>
            <p className="text-caption text-theme-text-hint mt-sm">
              {filterCount > 0
                ? 'Попробуйте изменить фильтры'
                : 'Попробуйте другую категорию или добавьте данные в Strapi'}
            </p>
          </div>
        )}
      </section>

      <div className="px-4">
        <ContactFooter />
      </div>

      {/* Filter Modal */}
      <FilterModal
        isOpen={isFilterModalOpen}
        onClose={handleCloseFilterModal}
        currentCategoryId={currentCategory?.id}
        selectedTags={selectedTagIds}
        onApplyFilters={handleApplyFilters}
      />
    </div>
  );
};

export default CategoryPage;
